import type { CalculatorField, CalculatorFieldValues } from 'components/Widget/shared/types';
import type { BudgetSection, BudgetStatus } from './types';

export const MAX_FIELD_VALUE = 999999;

export const RANGE_ERROR = 'Enter a number between 0 and 999,999';

/** Legacy used only a red border for empty or zero income; this message meets WCAG 1.4.1. */
export const INCOME_ERROR = 'Enter an income greater than $0';

export const DISCLAIMER =
  'This calculator is for educational purposes only and is not intended to offer any tax, legal, financial, or investment advice.';

export const INCOME_FIELD: CalculatorField = {
  label: 'Monthly after-tax income',
  hint: 'Your total monthly income after taxes and other paycheck deductions. If you have any automatic savings deductions, like 401(k) contributions, add that amount to your income so you can include those savings in your budget below.',
};

/* Duplicate legacy "Other" labels are qualified so every field has a distinct accessible name. */
export const BUDGET_SECTIONS: readonly BudgetSection[] = [
  {
    key: 'essentials',
    heading: 'Essentials',
    hint: 'Your nonnegotiable monthly expenses, like bills, housing, and transportation.',
    fields: [
      { label: 'Housing', hint: 'Mortgage, rent, taxes, etc.' },
      { label: 'Food', hint: 'Essential groceries and food.' },
      { label: 'Utilities', hint: 'Phone, water, gas, internet, etc.' },
      { label: 'Transportation', hint: 'Car maintenance, gas, public transportation, etc.' },
      { label: 'Health', hint: 'Medications, doctor visits, etc.' },
      { label: 'Insurance', hint: 'Car, home, or health insurance, etc.' },
      { label: 'Minimum loan payments', hint: 'Your monthly minimum loan payments, including your car loan.' },
      { label: 'Other essentials', hint: 'Any other required expenses (childcare, petcare, etc.)' },
    ],
  },
  {
    key: 'wants',
    heading: 'Wants',
    hint: 'Your monthly expenses for nonnecessities, like travel, entertainment, hobbies, etc.',
    fields: [{ label: 'Entertainment' }, { label: 'Dining out' }, { label: 'Travel' }, { label: 'Other wants' }],
  },
  {
    key: 'savings',
    heading: 'Savings',
    hint: 'Your monthly contribution toward savings, like building an emergency fund or saving for retirement.',
    fields: [
      {
        label: 'Emergency fund',
        hint: 'Your monthly contribution toward an emergency fund, ideally enough to cover three to six months of living expenses.',
      },
      {
        label: 'Retirement',
        hint: 'Your monthly contribution toward your long-term retirement savings, like in a 401(k) or IRA. Enter that number here if your employer automatically deducts it from your paycheck through an employer-sponsored retirement plan.',
      },
      {
        label: 'Other savings',
        hint: 'Enter any extra savings you may be saving here. (For example, if you’re paying more than the minimum monthly payment for any loans or debts, enter those extra funds here.)',
      },
    ],
  },
];

export const BUDGET_FIELDS: readonly CalculatorField[] = [
  INCOME_FIELD,
  ...BUDGET_SECTIONS.flatMap(section => section.fields),
];

/** Match legacy fields, which begin empty rather than at $0.00. */
export const INITIAL_VALUES: CalculatorFieldValues = Object.fromEntries(BUDGET_FIELDS.map(field => [field.label, '']));

export const DELTA_COPY: Record<BudgetStatus, string> = {
  under: 'You have an extra:',
  balanced: 'Your budget matches your income.',
  over: 'Your current spending exceeds your income by:',
};
